import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CustomCard from '../../../components/@extended/CustomCard';
import {
  Box,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CustomTableFoot,
  CustomTableHead,
  CustomTableHeadCell,
  CustomTableRow,
} from '../../../components/@extended/CustomTable';
import { useGetSiteInvoicesQuery } from '../../../store/slices/apiSlice';
import moment from 'moment';
import GenerateInvoice from './GenerateInvoice';
import CloseIcon from '@mui/icons-material/Close';
import CustomTablePagination from '../../../components/common/TablePagination';
import {
  getInvoiceDialogTitle,
  getInvoiceStatusColor,
  isInvoiceEditable,
} from './invoiceFeeUtils';
import CustomButton from '../../../components/@extended/CustomButton';

const columns = [
  {
    id: 'invoiceNumber',
    label: 'Invoice Number',
    minWidth: 170,
    align: 'left',
  },
  {
    id: 'invoiceDate',
    label: 'Invoice Date',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'dueDate',
    label: 'Due Date',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'amount',
    label: 'Amount',
    minWidth: 170,
    align: 'center',
  },
  { id: 'status', label: 'Status', minWidth: 170, align: 'center' },
];

const FinanceTrialSiteInvoicesCard = ({ setOpenGenerateInvoice }) => {
  const { trialId, siteId, trialSiteId } = useParams();
  const theme = useTheme();

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 5,
  });
  const { data } = useGetSiteInvoicesQuery(
    {
      params: {
        issuerEntityId: siteId,
        page: pagination.page,
        limit: pagination.rowsPerPage,
        clinicalStudyId: trialId,
        studySiteId: trialSiteId,
      },
    },
    { skip: !siteId }
  );

  const rows = useMemo(() => {
    if (!data) return [];

    return data?.data?.map((_inv) => ({
      invoiceNumber: _inv.invoiceDetails.invoiceNumber,
      invoiceDate: _inv.invoiceDetails.invoiceDate
        ? moment(_inv.invoiceDetails.invoiceDate, 'DD-MM-YYYY').format('DD MMM YYYY')
        : 'N/A',
      dueDate: _inv.invoiceDetails.dueDate
        ? moment(_inv.invoiceDetails.dueDate, 'DD-MM-YYYY').format('DD MMM YYYY')
        : 'N/A',
      amount: _inv?.amounts?.amount,
      tax: _inv?.amounts?.taxes,
      stripeFees: _inv?.amounts?.stripeCharges,
      applicationFees: _inv?.amounts?.transactionHandlingCost,
      totalAmount: _inv?.amounts?.amount,
      status: _inv?.invoiceDetails?.status,
      id: _inv?.invoiceDetails?.id || _inv?.items[0]?.invoiceId,
    }));
  }, [data]);

  const [openInvoiceDetails, setOpenInvoiceDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('');

  const onNewPage = (page, rowsPerPage) => {
    setPagination({ page, rowsPerPage });
  };

  const dialogTitle = getInvoiceDialogTitle(selectedInvoiceStatus, true);
  const canEditSelected = isInvoiceEditable(selectedInvoiceStatus);

  return (
    <>
      <CustomCard title="Invoices"
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <CustomButton variant="outlined" size="small" onClick={() => setOpenGenerateInvoice(true)}>
            Generate Invoice
          </CustomButton>
        }>
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="sticky table">
              <CustomTableHead>
                <TableRow>
                  {columns.map((column) => (
                    <CustomTableHeadCell key={column.id + column.label + 'columns'} align={column.align}>
                      {column.label}
                    </CustomTableHeadCell>
                  ))}
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {rows &&
                  rows?.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id || index}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedInvoice(row.id);
                          setSelectedInvoiceStatus(row.status);
                          setOpenInvoiceDetails(true);
                        }}
                      >
                        {columns.map((column) => {
                          if (column.id === 'amount') {
                            return (
                              <TableCell key={index + column.id} align={column.align}>
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="body2">Tax: ${row.tax ?? 0}</Typography>
                                      <Typography variant="body2">Stripe Fees: ${row.stripeFees ?? 0}</Typography>
                                      <Typography variant="body2">
                                        Application fees: ${row.applicationFees ?? 0}
                                      </Typography>
                                      <Typography variant="body2">Total: ${row.totalAmount ?? row.amount}</Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Typography variant="subtitle2" color="primary">
                                    ${row[column.id]}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            );
                          }
                          if (column.id === 'status') {
                            return (
                              <TableCell key={index + column.id} align={column.align}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ color: getInvoiceStatusColor(row.status, theme) }}
                                  fontWeight={600}
                                >
                                  {row[column.id]}
                                </Typography>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={row[column.id] + column.label + index + 'Site data'} align={column.align}>
                              <Typography variant="subtitle2" color="initial">
                                {row[column.id]}
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns?.length} align="center">
                      <Typography variant="subtitle2" color="initial">
                        No Invoices found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {data?.pagination?.total > pagination?.rowsPerPage && (
                <CustomTableFoot>
                  <CustomTableRow sx={{ right: 0 }}>
                    <CustomTableHeadCell colSpan={columns.length}>
                      <CustomTablePagination
                        page={pagination?.page}
                        rowsPerPage={pagination?.rowsPerPage}
                        count={data?.pagination?.total}
                        onNewPage={onNewPage}
                      />
                    </CustomTableHeadCell>
                  </CustomTableRow>
                </CustomTableFoot>
              )}
            </Table>
          </TableContainer>
        </CardContent>
      </CustomCard>
      <Dialog
        fullWidth
        maxWidth={'md'}
        scroll={'paper'}
        open={openInvoiceDetails}
        onClose={() => {
          setOpenInvoiceDetails(false);
          setSelectedInvoice('');
          setSelectedInvoiceStatus('');
        }}
        PaperProps={{
          sx: {
            maxWidth: 'md',
          },
        }}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            setOpenInvoiceDetails(false);
            setSelectedInvoice('');
            setSelectedInvoiceStatus('');
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <GenerateInvoice
            handleClose={() => {
              setOpenInvoiceDetails(false);
              setSelectedInvoice('');
              setSelectedInvoiceStatus('');
            }}
            isEditable={canEditSelected}
            isUpdate={true}
            invoiceId={selectedInvoice}
            invoiceStatus={selectedInvoiceStatus}
            trialId={trialId}
            trialSiteId={trialSiteId}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinanceTrialSiteInvoicesCard;
